/**
 * PR-D4-036 — Client session helpers (Google OAuth localStorage).
 */

export const AUTH_TOKEN_KEY = 'sat_auth_token';
export const AUTH_USER_KEY = 'sat_user';
/** Canonical anonymous device id — same key on /math-test and /dashboard. */
export const ANON_DEVICE_ID_KEY = 'sat_anon_device_id';
/** Legacy key from PR-D4-036; migrated into ANON_DEVICE_ID_KEY on read. */
const LEGACY_ANON_STUDENT_KEY = 'sat_anon_student_id';

let memoryAnonDeviceId: string | null = null;

export interface SatAuthUser {
    id: string;
    email: string;
    name: string | null;
    picture: string | null;
    role: string;
}

export function readAuthToken(): string | null {
    try {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        return token && token.trim() ? token : null;
    } catch {
        return null;
    }
}

export function readAuthUser(): SatAuthUser | null {
    try {
        const raw = localStorage.getItem(AUTH_USER_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<SatAuthUser>;
        if (!parsed || typeof parsed !== 'object') return null;
        if (typeof parsed.id !== 'string' || typeof parsed.email !== 'string') return null;
        return {
            id: parsed.id,
            email: parsed.email,
            name: typeof parsed.name === 'string' ? parsed.name : null,
            picture: typeof parsed.picture === 'string' ? parsed.picture : null,
            role: typeof parsed.role === 'string' ? parsed.role : 'student',
        };
    } catch {
        return null;
    }
}

export function isAuthenticated(): boolean {
    return Boolean(readAuthToken() && readAuthUser());
}

export function displayNameForUser(user: SatAuthUser): string {
    const name = user.name?.trim();
    if (name) return name;
    return user.email;
}

export function avatarInitialForUser(user: SatAuthUser): string {
    const source = (user.name?.trim() || user.email || '?').trim();
    return source.charAt(0).toUpperCase() || '?';
}

/** Clear session and return to landing. Safe to call from any route. */
export function handleLogout(): void {
    try {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_USER_KEY);
    } catch {
        // ignore storage failures
    }

    try {
        window.google?.accounts?.id?.disableAutoSelect?.();
    } catch {
        // GIS may be absent outside landing
    }

    window.location.href = '/';
}

function persistAnonDeviceId(id: string): string {
    memoryAnonDeviceId = id;
    try {
        localStorage.setItem(ANON_DEVICE_ID_KEY, id);
        localStorage.removeItem(LEGACY_ANON_STUDENT_KEY);
    } catch {
        // Private / blocked storage: keep the in-memory id for this tab.
    }
    return id;
}

function readStoredAnonDeviceId(): string | null {
    try {
        const current = localStorage.getItem(ANON_DEVICE_ID_KEY);
        if (current && current.trim()) {
            memoryAnonDeviceId = current.trim();
            return memoryAnonDeviceId;
        }
        const legacy = localStorage.getItem(LEGACY_ANON_STUDENT_KEY);
        if (legacy && legacy.trim()) return persistAnonDeviceId(legacy.trim());
        return null;
    } catch {
        return memoryAnonDeviceId;
    }
}

/** Stable practice identity: Google user id, else a persisted anonymous device id. */
export function resolvePracticeUserId(preferredId?: string | null): string {
    const user = readAuthUser();
    if (user?.id) return user.id;

    const stored = readStoredAnonDeviceId();
    if (stored) return persistAnonDeviceId(stored);

    const generated =
        (preferredId && preferredId.trim()) ||
        memoryAnonDeviceId ||
        `student-${Math.random().toString(36).slice(2, 12)}`;
    return persistAnonDeviceId(generated);
}

/**
 * PR-D4-042 / PR-D4-063 telemetry SSOT:
 * authenticated Google `users.id` always wins.
 * When logged out, the persisted `sat_anon_device_id` wins over URL `studentId`
 * so /math-test and /dashboard never key drafts to different identities.
 */
export function resolveTelemetryUserId(urlStudentId?: string | null): string {
    const user = readAuthUser();
    if (user?.id) return user.id;
    return resolvePracticeUserId(urlStudentId);
}
